import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import _ from "lodash";

// MUI Components
import {
  Button,
  Form,
  FormGroup,
  FormLabel,
} from "react-bootstrap";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import FeaturesIcon from "@mui/icons-material/Extension";
import { CloudDownload as CloudDownloadIcon } from "lucide-react";
import Multiselect from "multiselect-react-dropdown";
// Custom Components & API
import { bulkUserUpload, getSignedUrl } from "ServerAPI/serverAPI";
import { roleFeatureMapping, roleIdWithName } from "constants/Constants";
import FormSelect from "components/elements/form-select/FormSelect";
import { useToast } from "context/toast-context/ToastProvider";
import UploadFile from "components/courses/add-new-course/steps/UploadFile";

const BulkUserUpload = ({
  fetchUsers,
  departments,
  features,
  setSelectedFeatures,
  selectedFeatures,
  organizationDetails,
  handleClose,
}) => {
  const [cookies] = useCookies();
  const { showToast } = useToast();

  const [userRoles, setUserRoles] = useState([]);
  const [roleDetails, setRoleDetails] = useState({});
  const [allDepartments, setAllDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");

  // State for the new UploadFile component and feature filtering
  const [selectedFile, setSelectedFile] = useState(null);
  const [filteredFeatures, setFilteredFeatures] = useState([]);

  // Loading states
  const [fileUploadGoingOn, setFileUploadGoingOn] = useState(false);
  const [downloadTemplateLoading, setDownloadTemplateLoading] = useState(false);

const [submitLoader, setSubmitLoader] = useState(false);
  // Helper function to filter features based on the selected role
  const filterFeaturesByRole = (selectedRoleId, allFeatures) => {
    if (!selectedRoleId || !allFeatures) return [];
    const allowedFeatures = roleFeatureMapping[selectedRoleId] || [];
    return allFeatures.filter((feature) =>
      allowedFeatures.includes(feature.label)
    );
  };

  // Effect to populate user roles dropdown
  useEffect(() => {
    const roles = _.compact(
      roleIdWithName.map((role) => {
        if (role.id !== cookies.roleId && role.id !== "platformCoordinator") {
          return { value: role.id, label: role.name };
        }
        return null;
      })
    );
    setUserRoles(roles);
  }, [cookies.roleId]);

  // Effect to populate departments dropdown
  useEffect(() => {
    const result = departments?.map((department) => ({
      value: department.title,
      label: department.title,
    }));
    setAllDepartments(result || []);
  }, [departments]);

  // Effect to filter features when the role changes
  useEffect(() => {
    const selectedRole = roleDetails?.value;
    const filtered = filterFeaturesByRole(selectedRole, features);
    setFilteredFeatures(filtered);

    // Clear any selected features that are no longer valid for the new role
    if (selectedFeatures?.length > 0) {
      const validFeatures = selectedFeatures.filter((selectedFeature) =>
        filtered.some(
          (availableFeature) => availableFeature.value === selectedFeature.value
        )
      );
      if (validFeatures.length !== selectedFeatures.length) {
        setSelectedFeatures(validFeatures);
      }
    }
  }, [roleDetails?.value, features]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleRoleChange = (roleValue) => {
    const selectedRole = userRoles.find((ele) => roleValue === ele?.value);
    setRoleDetails(selectedRole || {});
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // Extract Base64 content
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setFileUploadGoingOn(true);

    try {
      const body = {
        file: await convertFileToBase64(selectedFile),
        organizationId: cookies.organizationId,
        department: selectedDepartment,
        userId: cookies.roleId,
        roleId: roleDetails.value,
        roleName: roleDetails.label,
        featureIds: selectedFeatures.map((feature) => feature?.value),
        sso: organizationDetails?.userManagementDetails?.ssoMode,
      };

      const response = await bulkUserUpload(body);

      if (response.status === 200) {
        showToast("Users created", "success");
        fetchUsers();
         handleClose(); 
        setFileUploadGoingOn(false);
      } else {
        showToast(response?.data?.message || "Error in creating users", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast(error?.message || "Unexpected error occurred during upload", "error");
    } finally {
      setFileUploadGoingOn(false);
          setSubmitLoader(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloadTemplateLoading(true);
    try {
      const payload = {
        method: "GET",
        downloadTemplate: true,
        fileName: "bulk_user_upload.xlsx",
        userId: cookies?.userId,
      };
      const response = await getSignedUrl(payload);
      if (response.status === 200) {
        const a = document.createElement("a");
        a.href = response?.data?.preSignedUrl;
        a.download = "bulk_user_upload.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Download error:", error);
      showToast("Failed to download template", "error");
    } finally {
      setDownloadTemplateLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <Form onSubmit={handleUpload}>
        {/* 2. Fields reordered: Department is now first */}
        <FormGroup className="mb-3">
          <FormLabel className="d-flex align-items-center">
            {/* 4. Added Icon */}
            <BusinessIcon style={{ color: "#224D99", marginRight: "8px" }} />
            Department
          </FormLabel>
          <FormSelect
            options={allDepartments}
            id="department"
            name="department"
            placeholder="Select Department"
            value={selectedDepartment}
            onChange={handleDepartmentChange}
            required
          />
        </FormGroup>

        <FormGroup className="mb-3">
          <FormLabel className="d-flex align-items-center">
            <WorkIcon style={{ color: "#224D99", marginRight: "8px" }} />
            Role
          </FormLabel>
          <FormSelect
            options={userRoles}
            id="role"
            name="role"
            placeholder="Select Role"
            value={roleDetails.value}
            onChange={(e) => handleRoleChange(e.target.value)}
            required
          />
        </FormGroup>

        {/* 3. Feature filtering logic applied */}
        <FormGroup className="mb-3">
          <FormLabel className="d-flex align-items-center">
            <FeaturesIcon style={{ color: "#224D99", marginRight: "8px" }} />
            Features
          </FormLabel>

          <Multiselect
            options={filteredFeatures}
            selectedValues={selectedFeatures}
            onSelect={(selectedList) => setSelectedFeatures(selectedList)}
            onRemove={(selectedList) => setSelectedFeatures(selectedList)}
            displayValue="label"
            placeholder={roleDetails?.value ? "Search features" : "Select a role first"}
            disable={!roleDetails?.value}
            showCheckbox={true}
            closeOnSelect={false}
            style={{
              option: {
                backgroundColor: '#f5f7fa', // very light gray blue
                color: '#2c3e50',           // dark slate blue
              },
              selectedOption: {
                backgroundColor: '#7fb3d5', // soft blue
                color: 'white',
              },
              chips: {
                backgroundColor: '#e3f2fd', // light blue-gray (like your multiValue)
                color: '#224D99',           // medium blue text
                fontWeight: '500',
              },
              searchBox: {
                border: '1px solid #ced4da',
                borderRadius: '8px',
                minHeight: '48px',
                padding: '8px',
              },
              optionContainer: {
                maxHeight: '180px',
                overflowY: 'auto',
              },
            }}
          />


        </FormGroup>

        {/* 1. Integrated new UploadFile component */}
        <UploadFile
          fileType="xlsx"
          handleFileChange={handleFileChange}
          showFileInfo={true}
        />

        <div className="d-flex justify-content-between align-items-center mt-5">
          <div>
            {downloadTemplateLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Tooltip title="Download Template">
                <IconButton onClick={downloadTemplate}>
                  <CloudDownloadIcon style={{ width: 20, height: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className="d-flex justify-content-end">
<Button type="submit" disabled={!selectedFile || submitLoader}>
  {submitLoader ? (
    <>
      <CircularProgress size={16} color="inherit" className="me-2" />
      Uploading...
    </>
  ) : (
    "Upload"
  )}
</Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default BulkUserUpload;